import React from 'react';
import { InputMode } from './InputModeToggle';
import NoteButtons from './NoteButtons';
import KeyButtons from './KeyButtons';
import CompactControlBar from './CompactControlBar';
import { ChallengeType } from '../challenge-types';

interface ChallengeSectionProps {
  challengeText: string;
  status: { state: string; message: string };
  isRecording: boolean;
  onMainAction: () => void;
  mainButtonText: string;
  mainButtonDisabled: boolean;
  isRunning: boolean;
  inputMode: InputMode;
  onInputModeChange: (mode: InputMode) => void;
  currentKey: string;
  onNoteSelect: (note: string) => void;
  challengeType: ChallengeType;
  onKeySelect: (key: string) => void;
  onChallengeTypeChange: (type: ChallengeType) => void;
}

const ChallengeSection: React.FC<ChallengeSectionProps> = ({ 
  challengeText, 
  status, 
  isRecording, // Used for visual feedback in audio mode
  onMainAction,
  mainButtonText,
  mainButtonDisabled,
  isRunning,
  inputMode,
  onInputModeChange,
  currentKey,
  onNoteSelect,
  challengeType,
  onKeySelect,
  onChallengeTypeChange
}) => {

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Control Bar - always visible */}
      <CompactControlBar
        challengeType={challengeType}
        onChallengeTypeChange={onChallengeTypeChange}
        inputMode={inputMode}
        onInputModeChange={onInputModeChange}
        onMainAction={onMainAction}
        mainButtonText={mainButtonText}
        mainButtonDisabled={mainButtonDisabled}
        isRunning={isRunning}
      />

      {/* Challenge Display - only show when running */}
      {isRunning && (
        <section 
          className={`
            bg-white/95 backdrop-blur-sm rounded-xl p-4 sm:p-6 shadow-lg border border-white/20
            transition-all duration-500 ease-in-out
            ${challengeText || status.state !== 'idle' 
              ? 'opacity-100 scale-100' 
              : 'opacity-0 scale-95'
            }
          `}
        >
          <div className="text-center space-y-3 sm:space-y-4">
            {/* Challenge Text - Larger and more prominent */}
            {challengeText && (
              <div>
                <h2
                  data-testid="challenge-text"
                  className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-slate-800 font-light leading-tight break-words"
                >
                  {challengeText}
                </h2>
              </div>
            )}

            {/* Minimal Status Indicator - Only show essential info */}
            {status.state === 'recording' && inputMode === 'audio' && (
              <div className="flex items-center justify-center">
                <div className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium animate-pulse border border-red-200">
                  🎤 Listening...
                </div>
              </div>
            )}
            
            {status.state === 'processing' && (
              <div className="flex items-center justify-center">
                <div className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium border border-amber-200">
                  Processing...
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Input Buttons - show in manual mode when running and ready for input */}
      {inputMode === 'manual' && isRunning && status.state === 'recording' && (
        <>
          {challengeType === ChallengeType.FORWARD ? (
            <NoteButtons
              currentKey={currentKey}
              onNoteSelect={onNoteSelect}
              disabled={false}
            />
          ) : (
            <KeyButtons
              onKeySelect={onKeySelect}
              disabled={false}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ChallengeSection;